import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { HolidayErrorMessage, HolidayModel } from '@core/services/holiday/holiday.model';
import { HolidayService } from '@core/services/holiday/holiday.service';
import { process } from '@progress/kendo-data-query';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { GridSettings } from '@shared/components/customized-grid/grid-settings/gird-settings.interface';
import { inputNoWhitespaceValidator } from '@shared/directives/input-no-whitespace.directive';
import { NgxSpinnerService } from 'ngx-spinner';
import { HolidayEditComponent } from './holiday-edit/holiday-edit.component';

@Component({
  selector: 'Teranet-Holiday',
  templateUrl: './holiday.component.html',
})
export class HolidayComponent implements OnInit {
  gridSettings: GridSettings;
  holidayErrorMessage = HolidayErrorMessage;
  holidayFormGroup: FormGroup;
  holidayModels: HolidayModel[] = [];
  services: any;

  constructor(
    private dialog: MatDialog,
    private holidayService: HolidayService,
    private spinner: NgxSpinnerService
  ) {
    this.holidayFormGroup = new FormGroup({
      code: new FormControl('', [inputNoWhitespaceValidator, Validators.required]),
      description: new FormControl('', [inputNoWhitespaceValidator, Validators.required]),
      name: new FormControl('', [inputNoWhitespaceValidator, Validators.required]),
    });
  }

  public state: any = {
    skip: 0,
    take: 13,
    filter: {
      logic: 'and',
      filters: [],
    },
  };

  ngOnInit() {
    this.gridSettings = this.gridDefaultSettings();
    this.refresh();
  }

  onDelete(row: HolidayModel) {
    let dialog =
      this.services && this.services[0] ? this.services[0] : null;

    let holidayService =
      this.services && this.services[1] ? this.services[1] : null;

    const dialogRef = dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: { message: `Do you want to delete the ${row.name} holiday?` },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        holidayService.deleteHoliday(row.id).subscribe(() => { });
      }
    });
  }

  onEdit(row: HolidayModel): void {
    let dialog =
      this.services && this.services[0] ? this.services[0] : null;

    const dialogRef = dialog.open(HolidayEditComponent, {
      width: '550px',
      data: row,
    });

    dialogRef.afterClosed().subscribe(() => { });
  }

  onSubmit() {
    if (this.holidayFormGroup.valid) {
      this.spinner.show();
      let holiday: HolidayModel = {
        code: this.holidayFormGroup.get('code').value,
        description: this.holidayFormGroup.get('description').value,
        id: 0,
        name: this.holidayFormGroup.get('name').value,
      };
      this.holidayService.insertOrUpdateHoliday(holiday).subscribe(result => {
        if (result) {
          this.holidayFormGroup.reset();
          this.refresh();
        }
      })
    }
  }

  refresh() {
    this.spinner.show();
    this.holidayModels = [];
    this.holidayService.getHolidays().subscribe(result => {
      if (result) {
        this.holidayModels = result;
        this.gridSettings.gridData = { data: this.holidayModels, total: this.holidayModels.length };
      }
      this.spinner.hide();
    });
  }

  gridDefaultSettings(): GridSettings {
    return {
      state: this.state,
      gridData: process(this.holidayModels, this.state),
      columnsConfig: [
        {
          field: 'code',
          filterable: true,
          hidden: false,
          title: 'Code',
          width: 150,
        },
        {
          field: 'name',
          filterable: true,
          hidden: false,
          title: 'Name',
          width: 150,
        },
        {
          field: 'description',
          filterable: true,
          hidden: false,
          title: 'Description',
          width: 150,
        },
        {
          clickAction: this.onEdit,
          field: 'id',
          filterable: false,
          hidden: false,
          icon: 'pen',
          includeInChooser: false,
          services: [this.dialog],
          title: 'Edit',
          type: 'icon',
          width: 40,
        },
        {
          clickAction: this.onDelete,
          field: 'id',
          filterable: false,
          hidden: false,
          icon: 'trash-alt',
          includeInChooser: false,
          services: [this.dialog, this.holidayService],
          title: 'Delete',
          type: 'icon',
          width: 40,
        },
      ],
    };
  }
}
